/* eslint-disable no-console */
import {Divider} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import BlockAccessPage from '../../../../components/BlockAccess/BlockAccessPage';
import Loader from '../../../../components/Loader/Loader';
import {checkIfHasPermissions} from '../../../../components/Menu/Utils/commonFunctions';
import PageContent from '../../../../components/PageContent/PageContent';
import OtherActivityTable from '../../../../components/Table/OtherActivityTable';
import MainTableTitle from '../../../../components/Table/Utils/MainTableTitle';
import Tabs from '../../../../components/Tabs/Tabs';
import {mainTitle} from '../../../../constants/mainTitle';
import {PermissionEnum} from '../../../../constants/permissionsEnum';
import TranslatedText from '../../../../libraries/I18N/TranslatedText';
import {APITypeEnum} from '../../../../services/interface/tableAPIType';
import country from '../../../../services/modules/JSON/country';
import efdbDatabaseDropdown from '../../../../services/modules/JSON/efdbDatabaseDropdown';
import facilityType from '../../../../services/modules/JSON/facilityType';
import facilityDetailsDropdown from '../../../../services/modules/OM/facilityDetails';
import yearFilterArray from '../../../../shared/YearFilterArray';
import useCompanyStore, {
  accessModule
} from '../../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../../../utils/commonFunctions';
import {shouldBlockModuleAccess} from '../../../../utils/SubscriptionHelper';
import SelectedCountryContext from '../../../configurator/orgFacility/Constants/SelectedCountryContext';

import {sortYearOptions} from './constant/sorYear';
import CapitalGoods from './Forms/CapitalGoods';
import SupplyChain from './Forms/SupplyChain';
import Travel from './Forms/Travel';
import Utility from './Forms/Utility';
import {IFacilityDetail} from './interface/commonI';
import FacilitySelect from './Utils/FacilitySelect';

const InfraProfile: React.FC = () => {
  const [searchParam, setSearchParams] = useSearchParams();
  const [selectedYear, setSelectedYear] = useState('');
  const {userDetails} = useUserStore.getState();
  const [efdbData, setEfdbData] = useState([]);
  const [facilityId, setFacilityId] = useState('');
  const [facilityDetail, setFacilityDetail] = useState<IFacilityDetail>({
    isLeased: false,
    country: 0,
    type: 0,
    label: '',
    value: ''
  });

  const facilities = useCompanyStore.getState().facilityDetails;

  const [selectedCountry, setSelectedCountry] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      facilityDetailsDropdown(APITypeEnum.GET_ALL, true),
      efdbDatabaseDropdown(),
      facilityType(),
      country()
    ])
      .then(([facilityDetails, efdbDatabaseResponse]) => {
        const facilityYears = sortYearOptions(
          yearFilterArray(
            facilityDetails,
            !(
              hasUserRole(UserRole.Company_Admin) ||
              hasUserRole(UserRole.Company_user)
            )
              ? userDetails?.facility
              : undefined
          )
        );
        const firstYearMain = facilityYears?.[0]?.value;
        const searchYear = searchParam.get('selectedYear') ?? '';
        const hasYear = facilityYears?.some((i: any) => i.value === searchYear);
        const setYear = hasYear ? searchYear : firstYearMain;
        searchParam.set('selectedYear', setYear);
        setSelectedYear(setYear);
        if (
          !(
            hasUserRole(UserRole.Company_Admin) ||
            hasUserRole(UserRole.Company_user)
          )
        ) {
          searchParam.delete('facility');
          setSearchParams(searchParam);
        } else {
          const hasFacility = facilityDetails?.[setYear]?.some((i: any) => {
            return i?._id?.toString() === searchParam.get('facility');
          });
          hasFacility
            ? searchParam.set('facility', searchParam.get('facility') ?? '')
            : searchParam.delete('facility');

          setFacilityId(hasFacility ? searchParam.get('facility') ?? '' : '');
        }
        setSearchParams(searchParam);
        setEfdbData(efdbDatabaseResponse);
      })
      .catch((err) => {
        console.error('Error occurred:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const IPTab = [
    {
      id: 'utility',
      name: <TranslatedText translationKey="Utilities" />,
      component: (
        <Utility
          selectedYear={selectedYear}
          facilityId={facilityId}
          facilityDetail={facilityDetail}
        />
      ),
      hidden: !checkIfHasPermissions(PermissionEnum.OrgManagerUtilities)
    },
    {
      id: 'supplyChain',
      name: <TranslatedText translationKey="Supply Chain & Transport" />,
      component: facilityDetail?.isLeased ? (
        <></>
      ) : (
        <SupplyChain selectedYear={selectedYear} facilityId={facilityId} />
      ),
      disabled: facilityDetail?.isLeased,
      hidden: !checkIfHasPermissions(PermissionEnum.OrgManagerSupplyChain)
    },
    {
      id: 'travel',
      name: <TranslatedText translationKey="Travel" />,
      component: facilityDetail?.isLeased ? (
        <></>
      ) : (
        <Travel selectedYear={selectedYear} facilityId={facilityId} />
      ),
      disabled: facilityDetail?.isLeased,
      hidden: !checkIfHasPermissions(PermissionEnum.OrgManagerTravel)
    },

    {
      id: 'capGoods',
      name: <TranslatedText translationKey="Capital Goods" />,
      component: facilityDetail?.isLeased ? (
        <></>
      ) : (
        <CapitalGoods selectedYear={selectedYear} facilityId={facilityId} />
      ),
      disabled: facilityDetail?.isLeased,
      hidden: !checkIfHasPermissions(PermissionEnum.OrgManagerCapitalGoods)
    },
    {
      id: 'otherActivity_scope3',
      name: <TranslatedText translationKey="Other Activities" />,
      component: shouldBlockModuleAccess(accessModule.scope3) ? (
        <BlockAccessPage />
      ) : facilityDetail?.isLeased ? (
        <></>
      ) : (
        <OtherActivityTable
          selectedYear={selectedYear}
          facilityId={facilityId}
        />
      ),
      disabled: facilityDetail?.isLeased,
      hidden: !checkIfHasPermissions(PermissionEnum.OrgManagerOtherActivities)
    }
  ].filter((item) => !item.hidden);

  const filterFacilityDetails = useMemo(() => {
    if (facilities?.[selectedYear]) {
      return facilities?.[selectedYear];
    } else {
      return [];
    }
  }, [selectedYear, facilities]);

  useEffect(() => {
    setFacilityDetail(
      filterFacilityDetails
        .filter((item: any) => {
          return item?._id === facilityId;
        })
        .map((item: any) => {
          return {
            isLeased: item.isLeased,
            country: item.country,
            type: item.type,
            label: item.name,
            value: item._id
          };
        })?.[0]
    );
  }, [filterFacilityDetails, facilityId]);

  useEffect(() => {
    setSelectedCountry(`${facilityDetail?.country ?? ''}`);
  }, [facilityDetail]);

  return (
    <PageContent>
      <SelectedCountryContext.Provider
        value={{
          selectedCountry,
          setSelectedCountry,
          efdbData,
          facilityId,
          selectedYear
        }}
      >
        {isLoading ? (
          <Loader />
        ) : (
          selectedYear !== '' && (
            <>
              <MainTableTitle title={mainTitle.infraProfile} />
              <FacilitySelect
                setFacilityId={setFacilityId}
                setSelectedYear={setSelectedYear}
                selectedYear={selectedYear}
              />
              <Divider />
              <Tabs array={IPTab} />
            </>
          )
        )}
      </SelectedCountryContext.Provider>
    </PageContent>
  );
};

export default InfraProfile;
